import { useCallback, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';

import { SWRFetcher } from '@commenda/utils';

import { IChatRoom } from '@commenda/types/src/chat-room/types';

import {
  AGENT_AUTO_REPLY_WATCH_INTERVAL_MS,
  AGENT_AUTO_REPLY_WATCH_MS,
  AGENT_REPLY_POLL_INTERVAL_MS,
  AGENT_REPLY_POLL_TIMEOUT_MS,
} from './constants';
import type { IAgentReplyPolling } from './types';
import type { ChatRoomChannel } from './updateChatRoomReadStatus';

export interface UseChatRoomParams {
  chatRoomId: string | null;
  token?: string;
  /** When set, only messages for this channel are requested (reduces payload when on one tab). */
  channel?: ChatRoomChannel;
}

export function useChatRoom({ chatRoomId, token, channel }: UseChatRoomParams) {
  const searchParams = new URLSearchParams();
  if (channel) {
    searchParams.set('channel', channel);
  }
  const query = searchParams.toString();

  const url =
    chatRoomId != null
      ? `chatRoom/${chatRoomId}${query ? `?${query}` : ''}`
      : null;

  const [agentReplyPolling, setAgentReplyPolling] =
    useState<IAgentReplyPolling | null>(null);

  const [autoReplyWatchStartedAt, setAutoReplyWatchStartedAt] = useState<
    number | null
  >(null);

  const refreshInterval = useCallback(() => {
    const now = Date.now();
    if (
      agentReplyPolling &&
      now - agentReplyPolling.startedAt < AGENT_REPLY_POLL_TIMEOUT_MS
    ) {
      return AGENT_REPLY_POLL_INTERVAL_MS;
    }
    if (
      autoReplyWatchStartedAt !== null &&
      now - autoReplyWatchStartedAt < AGENT_AUTO_REPLY_WATCH_MS
    ) {
      return AGENT_AUTO_REPLY_WATCH_INTERVAL_MS;
    }
    return 0;
  }, [agentReplyPolling, autoReplyWatchStartedAt]);

  const watchForAgentReply = useCallback(
    () => setAutoReplyWatchStartedAt(Date.now()),
    []
  );

  const {
    data,
    error,
    mutate,
  }: {
    data?: {
      chatRoom: IChatRoom;
    };
    error?: unknown;
    mutate: KeyedMutator<unknown>;
  } = useSWR(url, url ? SWRFetcher({ method: 'GET' }, token) : null, {
    // Always treat single-room data as stale so focus/reconnect actually refetch.
    revalidateOnFocus: true,
    // Reduce dedup window so a global mutate fired right after mark-as-read
    // isn't suppressed by SWR's default 2 s deduplication interval.
    dedupingInterval: 500,
    refreshInterval,
  });

  const pendingTriggerKey =
    data?.chatRoom?.agentState?.pendingTriggerMessageIds?.join(',') ?? '';
  if ((agentReplyPolling?.pendingTriggerKey ?? '') !== pendingTriggerKey) {
    setAgentReplyPolling(
      pendingTriggerKey ? { pendingTriggerKey, startedAt: Date.now() } : null
    );
  }

  return {
    data,
    error,
    mutate,
    watchForAgentReply,
  };
}
